import { type FC, useEffect, useMemo, useState } from "react";
import { Bookmark, Building2, ChevronDown, Lock, PlusIcon, Users } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/_atoms/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/_atoms/dropdown-menu";
import { OwnerTypes } from "@/domain/_shared/lib/owner-types";

import { useCurrentViewContext } from "../../hooks/use-current-view-context";
import { StoredView } from "../../models/stored-view-model";
import { TeamStoredViewInput, WorkspaceStoredViewInput } from "../../models/stored-view-type";

import { StoredViewCreateForm } from "./stored-view-create-form";
import { StoredViewEditForm } from "./stored-view-edit-form";
import { StoredViewListMenuItem } from "./stored-view-list-menu-item";
import { StoredViewListMenuItemBaseView } from "./stored-view-list-menu-item-base-view";
import { useStoredViewMenuContext } from "./stored-view-menu-context";
import { StoredViewModal } from "./stored-view-modal";

interface StoredViewListMenuProps {
  user: {
    id: string;
  };
  workspace: {
    id: string;
  };
  canBeShared: TeamStoredViewInput | WorkspaceStoredViewInput | null | undefined;
}

export const StoredViewListMenu: FC<StoredViewListMenuProps> = ({ user, workspace, canBeShared }) => {
  const { t } = useTranslation("common");
  const [dropdownOpen, setDropdownOpen] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [storedViewToEdit, setStoredViewToEdit] = useState<StoredView | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const { currentActiveView, baseDatatableState, currentStoredViewList, currentDatatableState, viewKey } =
    useCurrentViewContext();
  const { setLocalStoredViewId } = useStoredViewMenuContext();

  // Sync localStoredViewId with the active view optimistically so inline buttons
  // hide immediately when the user switches views (before the API responds).
  useEffect(() => {
    setLocalStoredViewId(currentActiveView?.storedViewId);
  }, [currentActiveView?.storedViewId, setLocalStoredViewId]);

  const userStoredViewList = useMemo(() => {
    return currentStoredViewList
      .slice()
      .filter((storedView) => storedView.ownerType === OwnerTypes.USER)
      .sort((a, b) => a.name.localeCompare(b.name))
      .sort((a, b) => (a.default ? -1 : b.default ? 1 : 0));
  }, [currentStoredViewList]);

  const workspaceStoredViewList = useMemo(() => {
    return currentStoredViewList
      .slice()
      .filter((storedView) => storedView.ownerType === OwnerTypes.WORKSPACE)
      .sort((a, b) => a.name.localeCompare(b.name))
      .sort((a, b) => (a.default ? -1 : b.default ? 1 : 0));
  }, [currentStoredViewList]);

  const teamStoredViewList = useMemo(() => {
    return currentStoredViewList
      .slice()
      .filter((storedView) => storedView.ownerType === OwnerTypes.TEAM)
      .sort((a, b) => a.name.localeCompare(b.name))
      .sort((a, b) => (a.default ? -1 : b.default ? 1 : 0));
  }, [currentStoredViewList]);

  return (
    <>
      <DropdownMenu
        onOpenChange={(open) => {
          if (!open) {
            setDropdownOpen(null);
          }
        }}
      >
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="xs"
            className="h-7 rounded-md border px-2 text-xs text-foreground/75"
          >
            <div className="flex items-center gap-1.5">
              <Bookmark className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="max-w-[150px] truncate whitespace-nowrap">
                {currentActiveView?.storedView?.name || t("view.baseView")}
              </span>
              <ChevronDown className="h-3 w-3 text-muted-foreground" />
            </div>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-[280px] p-1.5" align="end">
          <StoredViewListMenuItemBaseView
            datatableState={baseDatatableState}
            isActive={!currentActiveView?.storedViewId}
            onSelect={() => setLocalStoredViewId(null)}
          />

          {userStoredViewList.length > 0 && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="flex items-center gap-1.5 px-2 py-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground/70">
                <Lock className="h-3 w-3" />
                {t("view.private")}
              </DropdownMenuLabel>

              {userStoredViewList.map((storedView: StoredView) => (
                <StoredViewListMenuItem
                  key={storedView.id}
                  storedView={storedView}
                  isActive={currentActiveView?.storedViewId === storedView.id}
                  user={user}
                  onEdit={(storedView) => {
                    setStoredViewToEdit(storedView);
                    setEditDialogOpen(true);
                  }}
                  onSelect={() => setLocalStoredViewId(storedView.id)}
                  dropdownOpen={dropdownOpen}
                  setDropdownOpen={setDropdownOpen}
                />
              ))}
            </>
          )}
          {teamStoredViewList.length > 0 && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="flex items-center gap-1.5 px-2 py-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground/70">
                <Users className="h-3 w-3" />
                {t("view.team")}
              </DropdownMenuLabel>

              {teamStoredViewList.map((storedView: StoredView) => (
                <StoredViewListMenuItem
                  key={storedView.id}
                  storedView={storedView}
                  isActive={currentActiveView?.storedViewId === storedView.id}
                  user={user}
                  onEdit={(storedView) => {
                    setStoredViewToEdit(storedView);
                    setEditDialogOpen(true);
                  }}
                  onSelect={() => setLocalStoredViewId(storedView.id)}
                  dropdownOpen={dropdownOpen}
                  setDropdownOpen={setDropdownOpen}
                />
              ))}
            </>
          )}
          {workspaceStoredViewList.length > 0 && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="flex items-center gap-1.5 px-2 py-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground/70">
                <Building2 className="h-3 w-3" />
                {t("view.shared")}
              </DropdownMenuLabel>

              {workspaceStoredViewList.map((storedView: StoredView) => (
                <StoredViewListMenuItem
                  key={storedView.id}
                  storedView={storedView}
                  isActive={currentActiveView?.storedViewId === storedView.id}
                  user={user}
                  onEdit={(storedView) => {
                    setStoredViewToEdit(storedView);
                    setEditDialogOpen(true);
                  }}
                  onSelect={() => setLocalStoredViewId(storedView.id)}
                  dropdownOpen={dropdownOpen}
                  setDropdownOpen={setDropdownOpen}
                />
              ))}
            </>
          )}

          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="gap-1.5 px-2 text-xs text-muted-foreground"
            onSelect={() => {
              setStoredViewToEdit(null);
              setDropdownOpen(null);
              setCreateDialogOpen(true);
            }}
          >
            <PlusIcon className="h-3.5 w-3.5" />
            {t("view.createView")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <StoredViewModal
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        title={t("view.saveView")}
        description={t("view.saveViewDescription")}
      >
        <StoredViewCreateForm
          user={user}
          canBeShared={canBeShared}
          initialData={{
            name: t("view.newView"),
            default: false,
            workspaceId: workspace.id,
            key: viewKey,
            ...currentDatatableState,
          }}
          onSuccess={() => {
            setCreateDialogOpen(false);
          }}
          onCancel={() => {
            setCreateDialogOpen(false);
          }}
        />
      </StoredViewModal>

      {storedViewToEdit && (
        <StoredViewModal
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
          title={t("view.editView")}
          description={t("view.editViewDescription")}
        >
          <StoredViewEditForm
            canBeShared={canBeShared}
            initialData={storedViewToEdit}
            onSuccess={() => {
              setEditDialogOpen(false);
            }}
          />
        </StoredViewModal>
      )}
    </>
  );
};
